import { Table } from 'antd';
import Column from 'antd/es/table/Column';
import ColumnGroup from 'antd/es/table/ColumnGroup';
import { currencyFormat, formatVND, layTenNhomNhanSu } from '../../helpers/common';
import Input from 'antd/es/input/Input';
import React from 'react';

const romanNumerals = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];

interface FlatRow {
    key: string;
    type: 'grand-summary' | 'group-summary' | 'employee';
    groupName?: string;
    groupIndex?: string;
    employeeIndex?: number;
    originalIndex?: number;
    id?: number;
    hoTen: string | React.ReactNode;
    chucVu: string | React.ReactNode;
    bac?: string;
    heSoLuong?: number;
    phuCap?: number;
    mucLuongDongBaoHiem: number;
    nlD_BHXH: number;
    nlD_BHYT: number;
    nlD_BHTN: number;
    dV_BHXH: number;
    dV_BHYT: number;
    dV_BHTN: number;
    congDoan: number;
}

const buildFlatRows = (items: any[], dsNhomNhanSu?: any[]): FlatRow[] => {
    const flatRows: FlatRow[] = [];
    if (!items || items.length === 0) return flatRows;

    // Group items by idNhomNhanSu
    const groups: { [key: number]: { originalIndex: number; item: any }[] } = {};
    items.forEach((item, index) => {
        const nhomId = item.idNhomNhanSu || (item.isQuanLy ? 3 : 1);
        if (!groups[nhomId]) {
            groups[nhomId] = [];
        }
        groups[nhomId].push({ originalIndex: index, item });
    });

    let sortedGroupIds: number[] = [];
    const allGroupIds = Object.keys(groups).map(Number);

    if (dsNhomNhanSu && dsNhomNhanSu.length > 0) {
        const dbOrder = dsNhomNhanSu.map((x: any) => x.id);
        sortedGroupIds = [
            ...dbOrder.filter(id => allGroupIds.includes(id)),
            ...allGroupIds.filter(id => !dbOrder.includes(id))
        ];
    } else {
        const preferredOrder = [3, 7, 1];
        sortedGroupIds = [
            ...preferredOrder.filter(id => allGroupIds.includes(id)),
            ...allGroupIds.filter(id => !preferredOrder.includes(id))
        ];
    }

    // Compute Grand Totals
    let grandMucLuongDongBaoHiem = 0;
    let grandNldBhxh = 0;
    let grandNldBhyt = 0;
    let grandNldBhtn = 0;
    let grandDvBhxh = 0;
    let grandDvBhyt = 0;
    let grandDvBhtn = 0;
    let grandCongDoan = 0;

    items.forEach(item => {
        grandMucLuongDongBaoHiem += item.mucLuongDongBaoHiem || 0;
        grandNldBhxh += item.nlD_BHXH || 0;
        grandNldBhyt += item.nlD_BHYT || 0;
        grandNldBhtn += item.nlD_BHTN || 0;
        grandDvBhxh += item.dV_BHXH || 0;
        grandDvBhyt += item.dV_BHYT || 0;
        grandDvBhtn += item.dV_BHTN || 0;
        grandCongDoan += item.congDoan || 0;
    });

    // Add Grand Summary row at the very top
    flatRows.push({
        key: 'grand-summary',
        type: 'grand-summary',
        hoTen: 'Tổng cộng',
        chucVu: '',
        bac: undefined,
        heSoLuong: undefined,
        phuCap: undefined,
        mucLuongDongBaoHiem: grandMucLuongDongBaoHiem,
        nlD_BHXH: grandNldBhxh,
        nlD_BHYT: grandNldBhyt,
        nlD_BHTN: grandNldBhtn,
        dV_BHXH: grandDvBhxh,
        dV_BHYT: grandDvBhyt,
        dV_BHTN: grandDvBhtn,
        congDoan: grandCongDoan,
    });

    let employeeSTT = 1;

    sortedGroupIds.forEach((nhomId, groupIdx) => {
        const groupItems = groups[nhomId];
        let groupName = "";
        if (dsNhomNhanSu && dsNhomNhanSu.length > 0) {
            const matchedNhom = dsNhomNhanSu.find((x: any) => x.id === nhomId);
            groupName = matchedNhom ? matchedNhom.ten : layTenNhomNhanSu(nhomId);
        } else {
            groupName = nhomId === 7 ? "Trưởng Ban kiểm soát" : layTenNhomNhanSu(nhomId);
        }
        const groupRoman = romanNumerals[groupIdx] || (groupIdx + 1).toString();

        // Calculate Group Totals
        let groupMucLuongDongBaoHiem = 0;
        let groupNldBhxh = 0;
        let groupNldBhyt = 0;
        let groupNldBhtn = 0;
        let groupDvBhxh = 0;
        let groupDvBhyt = 0;
        let groupDvBhtn = 0;
        let groupCongDoan = 0;

        groupItems.forEach(({ item }) => {
            groupMucLuongDongBaoHiem += item.mucLuongDongBaoHiem || 0;
            groupNldBhxh += item.nlD_BHXH || 0;
            groupNldBhyt += item.nlD_BHYT || 0;
            groupNldBhtn += item.nlD_BHTN || 0;
            groupDvBhxh += item.dV_BHXH || 0;
            groupDvBhyt += item.dV_BHYT || 0;
            groupDvBhtn += item.dV_BHTN || 0;
            groupCongDoan += item.congDoan || 0;
        });

        // Add Group Summary Row
        flatRows.push({
            key: `group-summary-${nhomId}`,
            type: 'group-summary',
            groupIndex: groupRoman,
            hoTen: groupName,
            chucVu: '',
            bac: undefined,
            heSoLuong: undefined,
            phuCap: undefined,
            mucLuongDongBaoHiem: groupMucLuongDongBaoHiem,
            nlD_BHXH: groupNldBhxh,
            nlD_BHYT: groupNldBhyt,
            nlD_BHTN: groupNldBhtn,
            dV_BHXH: groupDvBhxh,
            dV_BHYT: groupDvBhyt,
            dV_BHTN: groupDvBhtn,
            congDoan: groupCongDoan,
        });

        // Add Group Employee Rows
        groupItems.forEach(({ originalIndex, item }) => {
            flatRows.push({
                key: `employee-${item.id}-${originalIndex}`,
                type: 'employee',
                employeeIndex: employeeSTT++,
                originalIndex,
                id: item.id,
                hoTen: item.hoTen,
                chucVu: item.chucVu,
                bac: item.bac,
                heSoLuong: item.heSoLuong,
                phuCap: item.phuCap,
                mucLuongDongBaoHiem: item.mucLuongDongBaoHiem,
                nlD_BHXH: item.nlD_BHXH,
                nlD_BHYT: item.nlD_BHYT,
                nlD_BHTN: item.nlD_BHTN,
                dV_BHXH: item.dV_BHXH,
                dV_BHYT: item.dV_BHYT,
                dV_BHTN: item.dV_BHTN,
                congDoan: item.congDoan,
            });
        });
    });

    return flatRows;
};

const TableBaoHiem = ({ loading, dataSource, loaiBaoHiemThang, isEdit, onChange, dsNhomNhanSu }: { loading: boolean, dataSource: any, loaiBaoHiemThang?: number, isEdit?: boolean, onChange?: (dataTable: any) => any, dsNhomNhanSu?: any[] }) => {
    const flatRows = buildFlatRows(dataSource, dsNhomNhanSu);

    return (
        <>
            <style>{`
                .table-row-grand-summary td {
                    background-color: #fff9e6 !important;
                    font-weight: bold;
                }
                .table-row-group-summary td {
                    background-color: #e6f7ff !important;
                    font-weight: bold;
                }
            `}</style>
            <Table
                sticky
                pagination={false}
                scroll={{ x: 1650 }}
                loading={loading}
                bordered={true}
                locale={{ emptyText: "Không có dữ liệu" }}
                dataSource={flatRows}
                rowClassName={(record: any) => {
                    if (record.type === 'grand-summary') return 'table-row-grand-summary';
                    if (record.type === 'group-summary') return 'table-row-group-summary';
                    return '';
                }}
            >
                <Column
                    fixed
                    title="STT"
                    width="70px"
                    key="stt"
                    render={(value, record: any) => {
                        if (record.type === 'grand-summary') return "";
                        if (record.type === 'group-summary') return <strong>{record.groupIndex}</strong>;
                        return record.employeeIndex;
                    }}
                />
                <Column title="Họ tên" dataIndex="hoTen" key="hoTen" fixed
                    render={(value, record: any) => {
                        if (record.type === 'grand-summary' || record.type === 'group-summary') {
                            return <strong>{value}</strong>;
                        }
                        return value;
                    }}
                />
                <Column title="Chức vụ" dataIndex="chucVu" key="chucVu" fixed
                    render={(value, record: any) => {
                        if (record.type === 'grand-summary' || record.type === 'group-summary') {
                            return "";
                        }
                        return value;
                    }}
                />
                <Column title={loaiBaoHiemThang == 1 ? "Chênh lệch Mức đóng BHXH" : "Mức đóng BHXH"} className="custom-column" dataIndex="mucLuongDongBaoHiem" key="mucLuongDongBaoHiem"
                    render={(value, record: any) => {
                        if (record.type === 'grand-summary' || record.type === 'group-summary') {
                            return <strong>{currencyFormat.formatMoney(value)}</strong>;
                        }
                        if (isEdit) {
                            return <Input value={currencyFormat.formatMoney(value)} onChange={(e) => {
                                if (onChange) {
                                    let v = 0;
                                    if (e.target.value == "" || e.target.value == "0") {
                                        v = 0;
                                    }
                                    else {
                                        v = parseInt(e.target.value.replace(/[^0-9-]/g, '').trim().replace(/^0+/, '').replace(/^(?=-)/, ''));
                                    }
                                    const origIdx = record.originalIndex!;
                                    dataSource[origIdx].mucLuongDongBaoHiem = v;
                                    dataSource[origIdx].nlD_BHXH = Math.round(v * 0.08);
                                    dataSource[origIdx].nlD_BHYT = Math.round(v * 0.015);
                                    dataSource[origIdx].nlD_BHTN = Math.round(v * 0.01);

                                    dataSource[origIdx].dV_BHXH = Math.round(v * 0.175);
                                    dataSource[origIdx].dV_BHYT = Math.round(v * 0.03);
                                    dataSource[origIdx].dV_BHTN = Math.round(v * 0.01);
                                    dataSource[origIdx].congDoan = Math.round(v * 0.02);
                                    onChange([...dataSource]);
                                }
                            }} />
                        }
                        return currencyFormat.formatMoney(value);
                    }} />
                <ColumnGroup title="Người lao động chi trả">
                    <Column title="BHXH" className="custom-column" dataIndex="nlD_BHXH" key="nlD_BHXH"
                        render={(value, record: any) => {
                            if (record.type === 'grand-summary' || record.type === 'group-summary') {
                                return <strong>{currencyFormat.formatMoney(value)}</strong>;
                            }
                            if (isEdit) {
                                return <Input value={currencyFormat.formatMoney(value)} onChange={(e) => {
                                    if (onChange) {
                                        let v = 0;
                                        if (e.target.value == "" || e.target.value == "0") {
                                            v = 0;
                                        }
                                        else {
                                            v = parseInt(e.target.value.replace(/[^0-9-]/g, '').trim().replace(/^0+/, '').replace(/^(?=-)/, ''));
                                        }
                                        dataSource[record.originalIndex!].nlD_BHXH = Math.round(v);
                                        onChange([...dataSource]);
                                    }
                                }} />
                            }
                            return currencyFormat.formatMoney(value);
                        }}
                    />
                    <Column title="BHYT" className="custom-column" dataIndex="nlD_BHYT" key="nlD_BHYT"
                        render={(value, record: any) => {
                            if (record.type === 'grand-summary' || record.type === 'group-summary') {
                                return <strong>{currencyFormat.formatMoney(value)}</strong>;
                            }
                            if (isEdit) {
                                return <Input value={currencyFormat.formatMoney(value)} onChange={(e) => {
                                    if (onChange) {
                                        let v = 0;
                                        if (e.target.value == "" || e.target.value == "0") {
                                            v = 0;
                                        }
                                        else {
                                            v = parseInt(e.target.value.replace(/[^0-9-]/g, '').trim().replace(/^0+/, '').replace(/^(?=-)/, ''));
                                        }
                                        dataSource[record.originalIndex!].nlD_BHYT = Math.round(v);
                                        onChange([...dataSource]);
                                    }
                                }} />
                            }
                            return currencyFormat.formatMoney(value);
                        }}
                    />
                    <Column title="BHTN" className="custom-column" dataIndex="nlD_BHTN" key="nlD_BHTN"
                        render={(value, record: any) => {
                            if (record.type === 'grand-summary' || record.type === 'group-summary') {
                                return <strong>{currencyFormat.formatMoney(value)}</strong>;
                            }
                            if (isEdit) {
                                return <Input value={currencyFormat.formatMoney(value)} onChange={(e) => {
                                    if (onChange) {
                                        let v = 0;
                                        if (e.target.value == "" || e.target.value == "0") {
                                            v = 0;
                                        }
                                        else {
                                            v = parseInt(e.target.value.replace(/[^0-9-]/g, '').trim().replace(/^0+/, '').replace(/^(?=-)/, ''));
                                        }
                                        dataSource[record.originalIndex!].nlD_BHTN = Math.round(v);
                                        onChange([...dataSource]);
                                    }
                                }} />
                            }
                            return currencyFormat.formatMoney(value);
                        }}
                    />
                    <Column title="Tổng" className="custom-column" key="nlD_tong"
                        render={(value: any, record: any) => {
                            const total = record.nlD_BHXH + record.nlD_BHYT + record.nlD_BHTN;
                            if (record.type === 'grand-summary' || record.type === 'group-summary') {
                                return <strong>{currencyFormat.formatMoney(total)}</strong>;
                            }
                            return currencyFormat.formatMoney(total);
                        }}
                    />
                </ColumnGroup>
                <ColumnGroup title="Người sử dụng lao động chi trả">
                    <Column title="BHXH" className="custom-column" dataIndex="dV_BHXH" key="dV_BHXH"
                        render={(value, record: any) => {
                            if (record.type === 'grand-summary' || record.type === 'group-summary') {
                                return <strong>{currencyFormat.formatMoney(value)}</strong>;
                            }
                            if (isEdit) {
                                return <Input value={currencyFormat.formatMoney(value)} onChange={(e) => {
                                    if (onChange) {
                                        let v = 0;
                                        if (e.target.value == "" || e.target.value == "0") {
                                            v = 0;
                                        }
                                        else {
                                            v = parseInt(e.target.value.replace(/[^0-9-]/g, '').trim().replace(/^0+/, '').replace(/^(?=-)/, ''));
                                        }
                                        dataSource[record.originalIndex!].dV_BHXH = Math.round(v);
                                        onChange([...dataSource]);
                                    }
                                }} />
                            }
                            return currencyFormat.formatMoney(value);
                        }}
                    />
                    <Column title="BHYT" className="custom-column" dataIndex="dV_BHYT" key="dV_BHYT"
                        render={(value, record: any) => {
                            if (record.type === 'grand-summary' || record.type === 'group-summary') {
                                return <strong>{currencyFormat.formatMoney(value)}</strong>;
                            }
                            if (isEdit) {
                                return <Input value={currencyFormat.formatMoney(value)} onChange={(e) => {
                                    if (onChange) {
                                        let v = 0;
                                        if (e.target.value == "" || e.target.value == "0") {
                                            v = 0;
                                        }
                                        else {
                                            v = parseInt(e.target.value.replace(/[^0-9-]/g, '').trim().replace(/^0+/, '').replace(/^(?=-)/, ''));
                                        }
                                        dataSource[record.originalIndex!].dV_BHYT = Math.round(v);
                                        onChange([...dataSource]);
                                    }
                                }} />
                            }
                            return currencyFormat.formatMoney(value);
                        }}
                    />
                    <Column title="BHTN" className="custom-column" dataIndex="dV_BHTN" key="dV_BHTN"
                        render={(value, record: any) => {
                            if (record.type === 'grand-summary' || record.type === 'group-summary') {
                                return <strong>{currencyFormat.formatMoney(value)}</strong>;
                            }
                            if (isEdit) {
                                return <Input value={currencyFormat.formatMoney(value)} onChange={(e) => {
                                    if (onChange) {
                                        let v = 0;
                                        if (e.target.value == "" || e.target.value == "0") {
                                            v = 0;
                                        }
                                        else {
                                            v = parseInt(e.target.value.replace(/[^0-9-]/g, '').trim().replace(/^0+/, '').replace(/^(?=-)/, ''));
                                        }
                                        dataSource[record.originalIndex!].dV_BHTN = Math.round(v);
                                        onChange([...dataSource]);
                                    }
                                }} />
                            }
                            return currencyFormat.formatMoney(value);
                        }}
                    />
                    <Column title="Tổng" className="custom-column" key="dV_tong"
                        render={(value: any, record: any) => {
                            const total = record.dV_BHXH + record.dV_BHYT + record.dV_BHTN;
                            if (record.type === 'grand-summary' || record.type === 'group-summary') {
                                return <strong>{currencyFormat.formatMoney(total)}</strong>;
                            }
                            return currencyFormat.formatMoney(total);
                        }}
                    />
                </ColumnGroup>
                <Column title="Tổng cộng BH phải nộp" className="custom-column" dataIndex="tc_BH" key="tc_BH"
                    render={(value, record: any) => {
                        const tong = record.dV_BHXH + record.dV_BHYT + record.dV_BHTN + record.nlD_BHTN + record.nlD_BHYT + record.nlD_BHXH;
                        if (record.type === 'grand-summary' || record.type === 'group-summary') {
                            return <strong>{currencyFormat.formatMoney(tong)}</strong>;
                        }
                        return currencyFormat.formatMoney(tong);
                    }}
                />
                <Column title="Kinh phí CĐ" className="custom-column" dataIndex="congDoan" key="congDoan"
                    render={(value, record: any) => {
                        if (record.type === 'grand-summary' || record.type === 'group-summary') {
                            return <strong>{currencyFormat.formatMoney(value)}</strong>;
                        }
                        if (isEdit) {
                            return <Input value={currencyFormat.formatMoney(value)} onChange={(e) => {
                                if (onChange) {
                                    const v = parseInt(e.target.value.replace(/[^0-9-]/g, '').trim().replace(/^0+/, '').replace(/^(?=-)/, ''));
                                    dataSource[record.originalIndex!].congDoan = v;
                                    onChange([...dataSource]);
                                }
                            }} />
                        }
                        return currencyFormat.formatMoney(value);
                    }}
                />
            </Table>
        </>
    );
};

export default TableBaoHiem;
